import { Translatable, TranslationGetter, translatable } from './translatable'
import { isObject } from './typeChecking'

const DICT_ID = 'core'

export function trCore(getter: TranslationGetter<CoreDict>, ...args: unknown[]): Translatable<CoreDict> {
  return translatable<CoreDict>(DICT_ID, getter, ...args)
}

export function isTrCore(translatable: unknown): translatable is Translatable<CoreDict> {
  return isObject(translatable) && (translatable as Translatable<unknown>).dictId === DICT_ID
}

export type CoreDict = {
  permissionDescriptions: {
    superuser: string
    users: {
      view: string
      manage: string
    }
    profile: {
      view: string
      manage: string
    }
    authCodes: {
      generate: string
      use: string
    }
    operatorSelfRegistration: string
    controlQuestions: {
      view: string
    }
    usersOfOwnMe: {
      view: string
      manage: string
    }
    roles: {
      view: string
      manage: string
    }
    globalSettings: {
      view: string
      manage: string
    }
    cardIssuePoints: {
      addOnlyME: string
      updateOnlyME: string
      deleteOnlyME: string
      viewAll: string
      viewOnlyME: string
      manageAll: string
      manageOnlyME: string
      tableExport: string
    }
    userCardIssuePoints: {
      view: string
      manage: string
    }
    citizens: {
      view: string
      meView: string
      manage: string
      meManage: string
      tableExport: string
    }
    municipalEntities: {
      view: string
      manage: string
    }
    salesPoints: {
      view: string
      meView: string
      manage: string
      meManage: string
      tableExport: string
    }
    usedCardsStats: {
      view: string
      meView: string
      manage: string
      meManage: string
    }
    citizenCategories: {
      view: string
      manage: string
    }
    events: {
      import: string
      export: string
    }
    actualIssueProducts: {
      meView: string
      meManage: string
      view: string
      manage: string
      tableExport: string
    }
    consumptionStandards: {
      view: string
      viewStats: string
    }
    consumptionRates: {
      view: string
      viewStats: string
      manage: string
    }
    issuanceCardsStats: {
      view: string
      viewOnlyME: string
      tableExport: string
    }
    usedCardStats: {
      view: string
      tableExport: string
    }
    citizenCategoriesCoefficients: {
      view: string
      manage: string
    }
    identityCards: {
      view: string
      manage: string
      viewOnlyME: string
      manageOnlyME: string
    }
    productPlan: {
      view: string
    }
    tools: {
      view: string
      export: string
      import: string
      exportMoView: string
      exportOptsView: string
    }
    productSupplies: {
      view: string
      manage: string
      tableExport: string
    }
    addressSuggester: {
      suggest: string
      importKladr: string
    }
  }
  validates: {
    validateOptionalString: {
      mustLeast: string
      mustNotGreater: string
      mustMatchPattern: string
    }
  }
  update: {
    downloadingUpdate: string
  }
  synchronization: {
    syncWithRemoteSourceOperation: string
    incompatibleRemoteSourceError: string
  }
  authCodes: {
    notFoundByIdError: string
    alreadyUsedByAnotherUserError: string
  }
  events: {
    accessDeniedToAnotherMEDataError: string
  }
  authorization: {
    invalidEmailOrPassword: string
    invalidEmailOrAnswer: string
    invalidEmailOrCode: string
    youAreNotAuthorized: string
    remoteAuthorizationNeeded: string
  }
  registration: {
    anotherUserAlreadyRegisteredOrCodeNotFoundError: string
  }
  permissions: {
    insufficientUserPermissionsError: string
  }
  users: {
    notFoundByIdError: string
    municipalEntityNotFoundError: string
    alreadyExistsError: string
    roleChangeNotAllowedError: string
    createWithSpecifiedRoleNotAllowedError: string
    selfDeletionNotAllowedError: string
  }
  roles: {
    roleExistsError: string
    emptyPermissionsError: string
    notFoundByIdError: string
  }
  municipalEntities: {
    notFoundByIdError: string
    alreadyExistsWithSameIndexError: string
    accessDenied: string
    notGiven: string
  }
  tools: {
    invalidTimeValueError: string
    invalidBooleanValueError: string
    serviceWasNotInitializedError: string
    exportData: string
    importData: string
    creatingAuthCode: string
    savingDistro: string
    copyData: string
    creatingMetaFile: string
    finalizeArchive: string
    readingArchive: string
    savingData: string
    exportError: string
    importError: string
    incorrectPath: string
    noMetaError: string
    fileNotFoundInArchiveError: string
    incompatibleDataSourceError: string
  }
  consumptionRates: {
    productCategoryDoesNotExist: string
  }
  cardIssuePoints: {
    notFoundByIdError: string
    municipalEntityAccessDenied: string
    alreadyExists: string
    invalidAddress: string
    hasUsers: string
    hasUsersOnMEChange: string
  }
  salesPoints: {
    municipalEntityNotFoundError: string
    notFoundByIdError: string
  }
  citizenCategories: {
    categoryDoesNotExist: string
  }
  identityCards: {
    notFoundByIdError: string
    alreadyExists: string
    completedOrLater: string
    noAccessToCardIssuePoint: string
  }
  citizens: {
    accessDenied: string
    alreadyExists: string
    notFoundByIdError: string
    municipalEntityNotFoundError: string
    cantBeTheSame: string
    guardianDoesntRelatedWithDependent: string
    dependentDoesntRelatedWithGuardian: string
    municipalEntityNotGiven: string
    municipalEntityAccessDenied: string
    guardianAlreadyHasDependent: string
    dependentAlreadyHasGuardian: string
  }
  globalSettings: {
    cardActivationDelayNotSet: string
  }
  productCategories: {
    categoryDoesNotExist: string
  }
}

export const CoreRuDict: CoreDict = {
  permissionDescriptions: {
    superuser: 'Суперпользователь, может всё',
    users: {
      view: 'Просмотр списка пользователей',
      manage: 'Управление списком пользователей',
    },
    profile: {
      view: 'Просмотр своего профиля',
      manage: 'Управление своим профилем',
    },
    controlQuestions: {
      view: 'Просмотр списка контрольных вопросов',
    },
    usersOfOwnMe: {
      view: 'Просмотр списка пользователей своего МО',
      manage: 'Управление списком пользователей своего МО',
    },
    roles: {
      view: 'Просмотр списка ролей',
      manage: 'Управление списком ролей',
    },
    authCodes: {
      generate: 'Генерация кода регистрации',
      use: 'Использование кода регистрации',
    },
    globalSettings: {
      view: 'Просмотр глобальных настроек',
      manage: 'Управление глобальными настройками',
    },
    cardIssuePoints: {
      viewAll: 'Просмотр списка всех пунктов выдачи карт',
      viewOnlyME: 'Просмотр списка пунктов выдачи карт своего МО',
      manageAll: 'Управление списком всех пунктов выдачи карт',
      manageOnlyME: 'Управление списком пунктов выдачи карт своего МО',
      addOnlyME: 'Создание пунктов выдачи карт в своём МО',
      updateOnlyME: 'Редактирование пунктов выдачи карт в своём МО',
      deleteOnlyME: 'Удаление пунктов выдачи карт в своём МО',
      tableExport: 'Выгрузка списочной формы пунктов выдачи карт',
    },
    userCardIssuePoints: {
      view: 'Просмотр пункта выдачи пользователя',
      manage: 'Управление пунктом выдачи пользователя',
    },
    citizens: {
      view: 'Просмотр списка граждан',
      meView: 'Просмотр списка граждан своего МО',
      manage: 'Управление списком граждан',
      meManage: 'Управление списком граждан своего МО',
      tableExport: 'Выгрузка списочной формы граждан',
    },
    municipalEntities: {
      view: 'Просмотр списка муниципальных образований',
      manage: 'Управление списком муниципальных образований',
    },
    salesPoints: {
      view: 'Просмотр списка торговых объектов',
      meView: 'Просмотр списка торговых объектов своего МО',
      manage: 'Управление списком торговых объектов',
      meManage: 'Управление списком торговых объектов своего МО',
      tableExport: 'Выгрузка списочной формы торговых объектов',
    },
    citizenCategories: {
      view: 'Просмотр списка категорий граждан',
      manage: 'Управление списком категорий граждан',
    },
    events: {
      export: 'Возможность экспортировать данные',
      import: 'Возможность импортировать данные',
    },
    actualIssueProducts: {
      view: 'Просмотр учета выдачи продовольствия',
      manage: 'Управление учетом выдачи продовольствия',
      meView: 'Просмотр учета выдачи продовольствия своего МО',
      meManage: 'Управление учетом выдачи продовольствия своего МО',
      tableExport: 'Выгрузка учетом выдачи продовольствия',
    },
    consumptionStandards: {
      view: 'Просмотр нормативов на талон',
      viewStats: 'Просмотр страницы с нормативами на талон',
    },
    consumptionRates: {
      view: 'Просмотр справочника норм потребления',
      viewStats: 'Просмотр страницы норм потребления',
      manage: 'Управление справочником норм потребления',
    },
    issuanceCardsStats: {
      view: 'Просмотр учёта выдачи идентификационных карт',
      viewOnlyME: 'Просмотр учёта выдачи идентификационных карт своего МО',
      tableExport: 'Выгрузка учёта выдачи идентификационных карт',
    },
    usedCardStats: {
      view: 'Просмотр учёта использования идентификационных карт',
      tableExport: 'Выгрузка учёта использования идентификационных карт',
    },
    citizenCategoriesCoefficients: {
      view: 'Просмотр справочника категорий граждан',
      manage: 'Редактирование справочника категорий граждан',
    },
    usedCardsStats: {
      view: 'Просмотр отчёта использованных карт',
      meView: 'Просмотр отчёта использованных карт своего МО',
      manage: 'Управление отчётом испрользованных карт',
      meManage: 'Управление отчётом испрользованных карт своего МО',
    },
    identityCards: {
      view: 'Просмотр идентификационных карт гражданина',
      manage: 'Управление идентификационными картами гражданина',
      viewOnlyME: 'Просмотр идентификационных карт гражданина своего МО',
      manageOnlyME: 'Управление идентификационными картами гражданина своего МО',
    },
    operatorSelfRegistration: 'Саморегистрация оператором',
    tools: {
      view: 'Просмотр инструментов',
      export: 'Просмотр инструментов экспорта данных',
      import: 'Просмотр инструментов импорта данных',
      exportMoView: 'Выбор муниципального образования для экспорта данных',
      exportOptsView: 'Выбор опций экспорта данных',
    },
    productPlan: {
      view: 'Просмотр продуктового плана',
    },
    productSupplies: {
      view: 'Просмотр списка поставок',
      manage: 'Управление поставками',
      tableExport: 'Выгрузка списка поставок',
    },
    addressSuggester: {
      suggest: 'Использовать справочник адресов для дополнения',
      importKladr: 'Импортировать справочник адресов КЛАДР',
    },
  },
  events: {
    accessDeniedToAnotherMEDataError: 'Доступ к данным другого муниципального образования запрещен',
  },
  validates: {
    validateOptionalString: {
      mustLeast: '{0}. Значение должно быть длиннее',
      mustNotGreater: '{0}. Значение должно быть короче',
      mustMatchPattern: '{0}. Значение не соответствует формату',
    },
  },
  update: {
    downloadingUpdate: 'Загрузка обновления',
  },
  synchronization: {
    syncWithRemoteSourceOperation: 'Синхронизация с внешним хранилищем',
    incompatibleRemoteSourceError: 'Несовместимое внешнее хранилище',
  },
  authorization: {
    invalidEmailOrPassword: 'Неверный email или пароль',
    invalidEmailOrAnswer: 'Неверный email или контрольный ответ',
    invalidEmailOrCode: 'Неверный email или авторизационный код',
    youAreNotAuthorized: 'Вы не авторизованы',
    remoteAuthorizationNeeded: 'Необходимо повторно авторизоваться для возобновления синхронизации данных',
  },
  registration: {
    anotherUserAlreadyRegisteredOrCodeNotFoundError:
      'Другой пользователь уже зарегистрирован или неверный код регистрации',
  },
  authCodes: {
    alreadyUsedByAnotherUserError: 'Код регистрации уже использован другим пользователем',
    notFoundByIdError: 'Код регистрации не найден по id={0}',
  },
  permissions: {
    insufficientUserPermissionsError: 'Недостаточно следующих прав: {0}',
  },
  users: {
    notFoundByIdError: 'Пользователь не найден по id={0}',
    municipalEntityNotFoundError: 'Мунициальное образование не найдено по id={0}',
    alreadyExistsError: 'Пользователь с Email "{0}" уже существует',
    roleChangeNotAllowedError: 'Не разрешено изменение роли',
    createWithSpecifiedRoleNotAllowedError: 'Создание пользователя с указанной ролью не разрешено',
    selfDeletionNotAllowedError: 'Нельзя удалить самого себя',
  },
  roles: {
    notFoundByIdError: 'Роль не найдена по id={0}',
    roleExistsError: 'Роль с именем "{0}" уже существует',
    emptyPermissionsError: 'Список разрешений не может быть пустым',
  },
  municipalEntities: {
    notFoundByIdError: 'Муниципальное образование не найдено по id={0}',
    accessDenied: 'Нет доступа к этому МО',
    notGiven: 'Муниципальное образование не указано',
    alreadyExistsWithSameIndexError: 'Муниципальное образование с индексом {0} уже существует',
  },
  tools: {
    invalidBooleanValueError: 'Некорректное логическое значение: {0}',
    serviceWasNotInitializedError: 'Сервис не инициализирован',
    exportData: 'Экспорт данных',
    importData: 'Импорт данных',
    savingDistro: 'Сохранение дистрибутива',
    copyData: 'Копирование данных',
    creatingAuthCode: 'Создание кода регистрации',
    creatingMetaFile: 'Создание файла метаданных',
    finalizeArchive: 'Завершение сохранения',
    readingArchive: 'Чтение архива',
    savingData: 'Сохранение данных',
    exportError: 'Ошибка экспорта данных',
    importError: 'Ошибка импорта данных',
    incorrectPath: 'Некорректный путь сохранения',
    invalidTimeValueError: 'Некорректное значение времени: {0}',
    noMetaError: 'Отсутствуют метаданные',
    fileNotFoundInArchiveError: 'Не найден файл в архиве: {0}',
    incompatibleDataSourceError: 'Несовместимый источник данных',
  },
  consumptionRates: {
    productCategoryDoesNotExist: 'Продуктовой категории «{0}» не существует в системе',
  },
  cardIssuePoints: {
    notFoundByIdError: 'Пункт выдачи карт не найден по id={0}',
    municipalEntityAccessDenied: 'Нет доступа к этому МО',
    alreadyExists: 'Номер пункта уже существует в данном муниципальном образовании',
    invalidAddress: 'Необходимо указать адрес или комментарий',
    hasUsers: 'Нельзя удалять пункт выдачи карт, в котором есть операторы',
    hasUsersOnMEChange: 'Нельзя менять МО в пункте выдачи карт, в котором есть операторы',
  },
  salesPoints: {
    notFoundByIdError: 'Торговый объект не найден по id={0}',
    municipalEntityNotFoundError: 'Муниципальное образование не найдено по id={0}',
  },
  citizenCategories: {
    categoryDoesNotExist: 'Категория граждан не существует',
  },
  productCategories: {
    categoryDoesNotExist: 'Продуктовая категория не существует',
  },
  identityCards: {
    notFoundByIdError: 'Идентификационная карта гражданина не найдена по id={0}',
    alreadyExists: 'Идентификационная карта гражданина c id={2} для {0}.{1} уже существует',
    completedOrLater: 'Идентификационная карта гражданина уже не активна или указана ещё не наступившая дата',
    noAccessToCardIssuePoint: 'Вы не можете выдавать идентификационные карты в этом пункте',
  },
  citizens: {
    accessDenied: 'Нет доступа к гражданину с id={0}',
    notFoundByIdError: 'Гражданин не найден по id={0}',
    alreadyExists: 'Гражданин с этим паспортом уже существует',
    municipalEntityNotFoundError: 'Муниципальное образование не найдено по id={0}',
    cantBeTheSame: 'Гражданин не может быть сам себе опекуном/иждивенцем',
    guardianDoesntRelatedWithDependent: 'Гражданин не является опекуном этого иждивенца',
    dependentDoesntRelatedWithGuardian: 'Гражданин не является иждивенцем этого опекуна',
    municipalEntityNotGiven: 'Муниципальный объект не получен',
    municipalEntityAccessDenied: 'Нет доступа к этому МО',
    guardianAlreadyHasDependent: 'Гражданин уже является опекуном этого иждивенца',
    dependentAlreadyHasGuardian: 'Гражданин уже является иждивенцем этого опекуна',
  },
  globalSettings: {
    cardActivationDelayNotSet: 'Не указано количество дней до начала месяца действия карты',
  },
}
